/**
 * GRIP Web 应用主体 —— 桌面版交互语义的 Web 移植（P4）
 * 状态机：空态 → 分析中 → 编辑态；单图流；IndexedDB 会话恢复。
 */
import "./app.css";
import { loadManifest, type ModelManifest } from "./infer/manifest";
import { ensureModel, listCached } from "./infer/modelLoader";
import { getSession } from "./infer/ort";
import type { AnalyzeRequest, InferMeta, InferWorkerMessage } from "./infer/worker";
import { analyze } from "./core/analyze";
import {
  defaultParams,
  type Analysis,
  type Cluster,
  type Hold,
  type Params,
} from "./core/types";
import {
  keepMask,
  feather,
  keepPerson,
  fillLayer,
  outlineBandFull,
  applyOutline,
  compositePreview,
  compositeFull,
} from "./core/render";
import { detectInRegion } from "./core/holds";
import { assignHold, clusterName, disambiguatedNames } from "./core/cluster";
import {
  saveSession,
  loadSession,
  deleteSession,
  sessionToAnalysis,
  type StoredSession,
} from "./core/session";
import { CanvasView, type ToolKind } from "./ui/canvas";

const HINTS: Record<ToolKind, string> = {
  pan: "平移：单指拖动（双指可缩放）",
  select: "选线：点击岩点选中其颜色线路；空白处无操作",
  lasso: "套索：圈选漏检区域补测；已选线路时新点直接并入",
  erase: "抹除：点击误检（不是岩点）→ 满强度抹平；再点恢复",
  exclude: "排除：点击颜色误判（是岩点但不入线路）→ 灰度化；再点恢复",
};

interface Snap {
  selected: number | null;
  excluded: Set<number>;
  erased: Set<number>;
  holds: Hold[];
  clusters: Cluster[];
}

/**
 * #15：worker 推理停滞（看门狗 terminate 后以此拒绝在途请求）。
 * runAnalysis 据此走降级链：Worker(默认) → Worker(单线程 wasm) → 主线程。
 */
class InferStallError extends Error {
  constructor(
    public stage: string,
    public seconds: number,
  ) {
    super(`推理停滞 ${seconds}s（${stage}）`);
  }
}

class App {
  private params: Params = defaultParams();
  private analysis: Analysis | null = null;
  private selected: number | null = null;
  private excluded = new Set<number>();
  private erased = new Set<number>();
  private undoStack: Snap[] = [];
  private view: CanvasView;
  private manifest: ModelManifest | null = null;
  private file: { name: string; blob: Blob; sha: string } | null = null;
  private busy = false;
  /** P1#1：openFile 重入守卫（consent 弹窗期间再选图会并发开两张） */
  private busyOpen = false;
  private scanTimer: number | null = null;
  private scanT0 = 0;
  private saveTimer: number | null = null;
  /** 攀岩者掩码 LUT（aw×ah，1=人物像素）：画布点击 O(1) 命中判定用 */
  private personLookup: Uint8Array | null = null;
  private syncDesktopPane: () => void = () => {};

  // T5 推理 Worker：null = 不可用（无 Worker 环境/构造或加载失败）→ 降级主线程路径
  private inferWorker: Worker | null = null;
  private inferWorkerTried = false;
  /** worker 已确认缓存的模型 id（bytes 只传一次，后续请求免重传） */
  private workerModelIds = new Set<string>();
  private workerSeq = 0;
  private workerPending: {
    id: number;
    resolve: (a: Analysis) => void;
    reject: (e: Error) => void;
  } | null = null;

  // #15 看门狗：worker 推理无 stage 进展超阈值（初始化 30s / 分析阶段 60s，
  // 分析阶段每 10 框必有进度消息，60s 静默几必为挂起）→ terminate 挂起的
  // worker 并拒绝在途请求，runAnalysis 的降级链接管自动重试。
  // 计时放主线程而非 worker 内：wasm 同步执行/微任务级联会饿死 worker 侧定时器，
  // 主线程事件循环空闲（推理已 Worker 化），是唯一能在挂起期间仍走表的表针。
  private workerWatchdog: number | null = null;
  private workerStageAt = 0;
  private workerSlowStage = "";
  /** 本页面生命周期内是否已用过单线程降级重试（防无限循环） */
  private inferDegradedTried = false;

  private startWorkerWatchdog(threads?: number): void {
    this.stopWorkerWatchdog();
    this.workerStageAt = performance.now();
    this.workerSlowStage = "启动推理引擎";
    // #15 第三轮：单线程 wasm 的会话创建（图优化/编译）在手机上可 legitimately
    // 超过 30s——那是慢不是挂。仅多线程/WebGPU 配置（已知挂起模式）保持 30s
    // 快速止损；单线程放宽到 150s，避免把正在编译的 worker 误杀后落入更慢的
    // 主线程路径（表象即「卡在 创建推理会话 i/N」）。
    const initTimeout = threads === 1 ? 150_000 : 30_000;
    this.workerWatchdog = window.setInterval(() => {
      const init =
        this.workerSlowStage.startsWith("创建推理会话") ||
        this.workerSlowStage === "启动推理引擎";
      const idleMs = performance.now() - this.workerStageAt;
      if (idleMs <= (init ? initTimeout : 60_000)) return;
      this.stopWorkerWatchdog(); // 每次分析至多触发一次
      const sec = Math.round(idleMs / 1000);
      const stage = this.workerSlowStage;
      console.warn(
        `[grip] 推理停滞：阶段「${stage}」已 ${sec}s 无进展` +
          "（疑似手机 wasm 多线程/WebGPU 挂起，#15/#1）。终止 Worker 降级重试",
      );
      // 恢复动作：terminate 挂起的 worker → 在途请求以 InferStallError 拒绝
      // → runAnalysis 降级链（单线程 Worker → 主线程）自动接管
      const w = this.inferWorker;
      this.inferWorker = null;
      this.inferWorkerTried = false; // 允许 ensureInferWorker 重建
      w?.terminate();
      this.workerModelIds.clear();
      const pend = this.workerPending;
      this.workerPending = null;
      if (pend) {
        pend.reject(new InferStallError(stage, sec));
      } else {
        this.toast(`推理停滞 ${sec}s（${stage}），请重试`);
      }
    }, 1000);
  }

  private stopWorkerWatchdog(): void {
    if (this.workerWatchdog !== null) {
      clearInterval(this.workerWatchdog);
      this.workerWatchdog = null;
    }
  }

  // ---------- #15 推理运行时降级 ----------
  /** 移动端启发式（与 720 档同源）：触屏粗指针 + 屏幕短边 <768px */
  private mobileHeuristic(): boolean {
    return (
      window.matchMedia("(pointer:coarse)").matches &&
      Math.min(window.screen.width, window.screen.height) < 768
    );
  }

  /** 曾触发降级（localStorage 钩子已置）→ 本设备已知不稳定，首跳即单线程 */
  private persistentDegraded(): boolean {
    try {
      return (
        localStorage.getItem("grip:ortThreads") === "1" ||
        localStorage.getItem("grip:ortEp") === "wasm"
      );
    } catch {
      return false;
    }
  }

  /** 持久化降级（下次页面加载直接走稳态配置；清除：删这两个 key） */
  private persistDegradedHooks(): void {
    try {
      localStorage.setItem("grip:ortThreads", "1");
      localStorage.setItem("grip:ortEp", "wasm");
    } catch {
      // localStorage 不可用：仅本次会话内降级
    }
  }

  /** Worker 首跳的运行时开关：移动端已知多线程 wasm / WebGPU 会话创建可能
   *  挂起（#15 真机两轮实锤：1/4 threaded-wasm 挂、3/4 webgpu decoder 挂）→
   *  首跳直接单线程 wasm（慢但稳，省 30s 停滞止损等待）；已知不稳设备同样 */
  private baseOrtOptions(): { ep: "wasm"; threads?: number } | undefined {
    if (this.persistentDegraded()) return { ep: "wasm", threads: 1 };
    if (this.mobileHeuristic()) return { ep: "wasm", threads: 1 };
    return undefined;
  }

  /** 会话恢复/分析完成后同步攀岩者保色 chip（检出人物才显示，保色开→.on） */
  private syncParamsSheet(): void {
    const btn = document.getElementById("btn-person") as HTMLButtonElement | null;
    if (!btn) return;
    const has = !!this.analysis?.person && this.analysis.person.length > 0;
    btn.hidden = !has;
    btn.classList.toggle("on", has && this.params.personKeep > 0);
  }

  /** 由 analysis.person 构建行优先 LUT（enterEdit：新分析与恢复会话两条路都经过） */
  private buildPersonLookup(): void {
    const a = this.analysis;
    this.personLookup = null;
    if (!a?.person || a.person.length === 0) return;
    const lut = new Uint8Array(a.aw * a.ah);
    for (const i of a.person) lut[i] = 1;
    this.personLookup = lut;
  }

  /** 攀岩者保色独立开关：chip 或画布点击人物区域切换 */
  private togglePersonKeep(): void {
    if (!this.analysis) return;
    this.params.personKeep = this.params.personKeep > 0 ? 0 : 1;
    this.refreshPreview();
    this.autosave();
    this.syncParamsSheet();
    this.toast(this.params.personKeep > 0 ? "攀岩者保色：开" : "攀岩者保色：关");
  }

  constructor() {
    this.view = new CanvasView({
      onTap: (p) => this.onTap(p),
      onLasso: (pts) => this.onLasso(pts),
      onZoomChange: () => this.updateTitle(),
    });
    this.bindChrome();
    this.view.resize();
    window.addEventListener("resize", () => {
      this.view.resize();
    });
    this.installDebugHook();
  }

  /**
   * e2e/诊断只读钩子（#13/#14 复现脚本用）：暴露归组状态与视图变换，
   * 使 Playwright 能把岩点 bbox 换算成屏幕坐标做像素级断言。生产无副作用。
   */
  private installDebugHook(): void {
    (window as unknown as { __gripDebug?: () => unknown }).__gripDebug = () => ({
      selected: this.selected,
      excluded: [...this.excluded],
      erased: [...this.erased],
      zoom: this.view.zoom,
      panX: this.view.panX,
      panY: this.view.panY,
      tool: this.view.tool,
      attrW: (document.getElementById("view") as HTMLCanvasElement).width,
      attrH: (document.getElementById("view") as HTMLCanvasElement).height,
      // #15：实际生效的分析档位与指针环境（移动端 tier/e2e 断言用）
      maxDim: this.params.analysisMaxDim,
      pointerCoarse: window.matchMedia("(pointer:coarse)").matches,
      clusters: this.analysis?.clusters.map((c) => ({
        kind: c.kind as string,
        rgb: c.rgb,
        holds: c.holds,
      })),
      holds: this.analysis?.holds.map((h) => ({
        id: h.id,
        cluster: h.cluster,
        bbox: h.bbox,
        centroid: h.centroid,
        area: h.area,
      })),
    });
  }

  // ---------- chrome 绑定 ----------
  private bindChrome(): void {
    const open = () => this.pickFile();
    const camera = () => this.pickFile(true);
    document.getElementById("btn-open")!.addEventListener("click", open);
    document.getElementById("btn-open-big")!.addEventListener("click", open);
    document.getElementById("btn-capture")!.addEventListener("click", camera);
    document.getElementById("btn-capture-big")!.addEventListener("click", camera);
    document.getElementById("btn-undo")!.addEventListener("click", () => this.undo());
    document.getElementById("btn-export")!.addEventListener("click", () => this.export());
    document.getElementById("btn-fit")!.addEventListener("click", () => this.view.fit());
    document.getElementById("btn-overlay")!.addEventListener("click", (e) => {
      const b = e.currentTarget as HTMLButtonElement;
      b.classList.toggle("on");
      this.view.overlayStyle.visible = b.classList.contains("on");
      this.view.draw();
    });

    document.querySelectorAll<HTMLButtonElement>(".tool").forEach((b) => {
      b.addEventListener("click", () => this.setTool(b.dataset.tool as ToolKind, b));
    });
    // 2026-10-22 用户决议：去掉「结果|原图」切换（与按住对比重复），保留按住对比
    const cmp = document.getElementById("btn-compare")!;
    cmp.addEventListener("pointerdown", () => this.view.setShowResult(false));
    const cmpUp = () => {
      if (this.selected !== null) this.view.setShowResult(true);
    };
    cmp.addEventListener("pointerup", cmpUp);
    cmp.addEventListener("pointerleave", cmpUp);

    document.getElementById("btn-person")!.addEventListener("click", () => {
      this.togglePersonKeep();
    });

    const sheet = document.getElementById("sheet-params")!;
    const backdrop = document.getElementById("sheet-backdrop")!;
    const desktopMq = window.matchMedia("(min-width: 1024px)");
    const syncDesktop = () => {
      if (desktopMq.matches && this.analysis) {
        // 桌面：参数面板常驻右栏
        sheet.hidden = false;
        backdrop.hidden = true;
      }
    };
    desktopMq.addEventListener("change", syncDesktop);
    this.syncDesktopPane = syncDesktop;
    document.getElementById("btn-params")!.addEventListener("click", () => {
      sheet.hidden = false;
      backdrop.hidden = false;
    });
    const closeSheet = () => {
      if (desktopMq.matches) return; // 桌面常驻不可关
      sheet.hidden = true;
      backdrop.hidden = true;
    };
    document.getElementById("btn-params-close")!.addEventListener("click", closeSheet);
    backdrop.addEventListener("click", closeSheet);

    (document.getElementById("tier-select") as unknown as HTMLSelectElement).addEventListener("change", (e) => {
      localStorage.setItem("grip:tier", (e.target as HTMLSelectElement).value);
      this.toast("性能档位已保存，重新检测后生效");
    });
    // ux-review P1：window.confirm 在内嵌浏览器会被吞 → 两步式确认按钮
    const rdBtn = document.getElementById("btn-redetect") as HTMLButtonElement;
    let rdArmed = false;
    let rdTimer = 0;
    rdBtn.addEventListener("click", () => {
      if (!rdArmed) {
        rdArmed = true;
        rdBtn.textContent = "再点一次确认丢弃标注";
        rdTimer = window.setTimeout(() => {
          rdArmed = false;
          rdBtn.textContent = "重新检测（丢弃本张标注）";
        }, 3000);
        return;
      }
      clearTimeout(rdTimer);
      rdArmed = false;
      rdBtn.textContent = "重新检测（丢弃本张标注）";
      closeSheet();
      void this.redetect();
    });
    this.buildParamsSheet();
    const tierSel = document.getElementById("tier-select") as unknown as HTMLSelectElement;
    tierSel.value = localStorage.getItem("grip:tier") ?? "auto";
  }

  private buildParamsSheet(): void {
    const g = document.getElementById("param-render-group")!;
    // 攀岩者保色已移出参数面板：独立 chip（#btn-person，画布点击人物区域亦可切换）
    const mkSlider = (
      parent: HTMLElement,
      label: string,
      key: keyof Params,
      min: number,
      max: number,
      step: number,
      fmt: (v: number) => string,
      hint?: string,
      logScale = false,
      instant = true,
    ) => {
      const row = document.createElement("div");
      row.className = "sheet-row";
      const toSlider = (v: number) => (logScale ? Math.log10(v) : v);
      const fromSlider = (v: number) => (logScale ? Math.pow(10, v) : v);
      row.innerHTML = `<label>${label}</label><input type="range" min="${toSlider(min)}" max="${toSlider(max)}" step="${step}" value="${toSlider(this.params[key] as number)}"><span class="val mono">${fmt(this.params[key] as number)}</span>`;
      const input = row.querySelector("input")!;
      const val = row.querySelector(".val")!;
      input.addEventListener("input", () => (val.textContent = fmt(fromSlider(Number(input.value)))));
      input.addEventListener("change", () => {
        (this.params[key] as number) = fromSlider(Number(input.value));
        if (instant) {
          this.refreshPreview();
          this.autosave();
        } else {
          this.autosave();
          this.toast("已记录，重新检测后生效");
        }
      });
      if (hint) {
        const p = document.createElement("p");
        p.className = "hint-small";
        p.textContent = hint;
        row.appendChild(p);
      }
      parent.appendChild(row);
    };
    mkSlider(g, "边缘羽化", "feather", 0, 8, 0.5, (v) => v.toFixed(1));
    mkSlider(g, "背景保留饱和度", "bgSat", 0, 0.35, 0.01, (v) => v.toFixed(2), "0 = 完全灰度");
    mkSlider(g, "墙面净化", "bgFill", 0, 1, 0.05, (v) => Math.round(v * 100) + "%", "抹平脏墙与螺丝孔；0=关闭");
    mkSlider(g, "描边高亮", "outline", 0, 1, 0.05, (v) => Math.round(v * 100) + "%", "选中线路描橙色边；灰点在灰墙上也可辨");
    const adv = document.getElementById("param-adv-group")!;
    mkSlider(adv, "灵敏度", "wallK", 3, 10, 0.1, (v) => v.toFixed(1), "越小检出越多", false, false);
    mkSlider(adv, "最小岩点", "minAreaFrac", 1.2e-4, 0.0063, 0.05, (v) => (v * 100).toFixed(3) + "%", "占画面比例", true, false);
  }

  private setTool(t: ToolKind, btn?: HTMLButtonElement): void {
    this.view.tool = t;
    document.querySelectorAll(".tool").forEach((x) => x.classList.remove("on"));
    (btn ?? document.querySelector(`.tool[data-tool="${t}"]`))!.classList.add("on");
    // 2026-10-22 用户决议：功能提示改为切换时短暂显示（2.6s 后自动消失）
    this.flashHint(HINTS[t]);
  }

  /** 工具提示短暂显示：文本上屏 → 定时淡出（不常驻占视觉） */
  private hintTimer: number | null = null;
  private flashHint(text: string): void {
    const hint = document.getElementById("hint-bar")!;
    hint.textContent = text;
    hint.hidden = false;
    if (this.hintTimer) clearTimeout(this.hintTimer);
    this.hintTimer = window.setTimeout(() => {
      hint.hidden = true;
      this.hintTimer = null;
    }, 2600);
  }

  // ---------- 文件与分析 ----------
  /** 选图/拍摄统一入口；camera=true 时经 capture=environment 调起后置相机
   *  （移动端生效；桌面浏览器忽略该属性，回落为普通文件选择） */
  private pickFile(camera = false): void {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/jpeg,image/png,image/webp,image/bmp";
    if (camera) input.capture = "environment";
    input.onchange = () => {
      const f = input.files?.[0];
      if (f) void this.openFile(f);
    };
    input.click();
  }

  private async openFile(f: File): Promise<void> {
    // P1#1：busy 只在 runAnalysis 内置位，consent 弹窗等待期 busy=false，
    // 再选图会并发开两张（后者覆盖 this.file）。busyOpen 覆盖 openFile 全程。
    if (this.busy || this.busyOpen) return;
    this.busyOpen = true;
    try {
      this.file = { name: f.name, blob: f, sha: "" };
      this.updateTitle();
      // #15：选定照片即切换画布为本图（预览），不再停留在首页/上传按钮背景
      document.getElementById("empty-state")!.hidden = true;
      this.showScan("读取照片");
      const buf = await f.arrayBuffer();
      void this.setPhotoPreview(buf); // 异步非阻塞；失败静默（仅预览）
      const digest = await crypto.subtle.digest("SHA-256", buf);
      this.file.sha = Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");

      // 首次载入流量提醒（T11/#10）：未缓存模型时先征求同意，拒绝则零模型流量
      const pendingBytes = await this.uncachedModelBytes();
      if (pendingBytes > 0) {
        const ok = await this.consentDownload(pendingBytes);
        if (!ok) {
          this.file = null;
          this.updateTitle();
          this.hideScan();
          document.getElementById("empty-state")!.hidden = false;
          return;
        }
      }

      await this.ensureModels();

      // 会话恢复优先
      const stored = await loadSession(this.file.sha).catch(() => undefined);
      if (stored && stored.params.analysisMaxDim === this.effectiveMaxDim()) {
        await this.restoreFrom(stored);
        this.toast("已恢复上次标注");
        return;
      }
      await this.runAnalysis(buf);
    } finally {
      this.busyOpen = false;
    }
  }

  /**
   * 选图后立即把照片画上画布（#15：分析期间背景即所选图片，而非首页/深底）。
   * 分辨率与最终分析一致（同 effectiveMaxDim 缩放），分析完成/会话恢复后由
   * setAnalysis 无缝替换。仅设 view 的显示层，this.analysis 保持 null（不可交互）。
   */
  private async setPhotoPreview(buf: ArrayBuffer): Promise<void> {
    try {
      const bmp = await createImageBitmap(new Blob([buf]));
      const scale = Math.min(this.effectiveMaxDim() / Math.max(bmp.width, bmp.height), 1);
      const aw = Math.min(Math.max(Math.round(bmp.width * scale), 32), bmp.width);
      const ah = Math.min(Math.max(Math.round(bmp.height * scale), 32), bmp.height);
      const cv = document.createElement("canvas");
      cv.width = aw;
      cv.height = ah;
      const cctx = cv.getContext("2d", { willReadFrequently: true })!;
      cctx.drawImage(bmp, 0, 0, aw, ah);
      const img = cctx.getImageData(0, 0, aw, ah).data;
      this.view.setAnalysis({
        aw,
        ah,
        fw: bmp.width,
        fh: bmp.height,
        labels: new Uint32Array(0),
        holds: [],
        clusters: [],
        img,
        hue: new Float32Array(0),
        sat: new Float32Array(0),
        val: new Float32Array(0),
        person: null,
        wallV: 0.5,
        wallS: 0.15,
        timingMs: 0,
      });
    } catch {
      // 预览失败不影响分析流程（画布保持深底）
    }
  }

  /** 从存储会话恢复到编辑态（不加载模型，秒开） */
  async restoreFrom(stored: StoredSession): Promise<void> {
    localStorage.setItem("grip:last", stored.id);
    this.file = { name: stored.name, blob: stored.origBlob, sha: stored.id };
    this.updateTitle();
    this.hideScan();
    this.params = stored.params;
    this.selected = stored.selected;
    this.excluded = new Set(stored.excluded);
    this.erased = new Set(stored.erased);
    this.analysis = sessionToAnalysis(stored);
    this.undoStack = [];
    this.enterEdit(stored.timingMs, true);
  }

  private effectiveMaxDim(): number {
    const t = localStorage.getItem("grip:tier") ?? "auto";
    if (t === "720" || t === "1600") return Number(t); // 手动选档优先（#15：手机手动 1600 不被压回）
    // #15：现代手机 hardwareConcurrency 常 ≥8、deviceMemory 多为 undefined→8，
    // 硬件启发式在手机上几乎必然误判 1600 档——250 框串行 SAM 解码在手机
    // wasm 上可达数分钟，表象即「初始化后一直卡在 SAM 掩码解码」。触屏粗指针
    // + 小屏（短边 <768px）启发式直接压回 720 档（平板/触屏笔记本大屏不误伤）。
    if (this.mobileHeuristic()) return 720;
    const cores = navigator.hardwareConcurrency ?? 8;
    const mem = (navigator as { deviceMemory?: number }).deviceMemory ?? 8;
    return cores <= 4 || mem <= 4 ? 720 : 1600;
  }

  /** 未缓存模型的总字节数（0 = 全部命中，无需提醒） */
  private async uncachedModelBytes(): Promise<number> {
    if (!this.manifest) this.manifest = await loadManifest();
    const cached = await listCached().catch(() => []);
    const cachedIds = new Set(cached.map((c) => c.id));
    let sum = 0;
    for (const [id, e] of Object.entries(this.manifest.models)) {
      if (!cachedIds.has(id)) sum += e.bytes;
    }
    return sum;
  }

  /** 流量提醒弹窗：resolve true=继续下载 */
  private consentDownload(pendingBytes: number): Promise<boolean> {
    const modal = document.getElementById("consent-modal")!;
    document.getElementById("consent-size")!.textContent =
      `${(pendingBytes / 1048576).toFixed(0)} MB`;
    modal.hidden = false;
    return new Promise((resolve) => {
      const yes = document.getElementById("consent-yes")!;
      const no = document.getElementById("consent-no")!;
      const done = (v: boolean) => {
        modal.hidden = true;
        yes.replaceWith(yes.cloneNode(true));
        no.replaceWith(no.cloneNode(true));
        resolve(v);
      };
      yes.addEventListener("click", () => done(true), { once: true });
      no.addEventListener("click", () => done(false), { once: true });
    });
  }

  // ---------- T5 推理 Worker（主路；无 Worker 环境降级主线程） ----------
  /** 惰性创建推理 Worker；不可用（无 Worker 环境/构造失败）返回 null */
  private ensureInferWorker(): Worker | null {
    if (this.inferWorkerTried) return this.inferWorker;
    this.inferWorkerTried = true;
    if (typeof Worker === "undefined") {
      // 无 Worker 环境（文档承诺的降级）：ORT wasm 的 pthread 同样依赖 Worker，
      // 经 ort.ts 的 grip:ortThreads 降档钩子强制单线程（须在首次 getSession 前写入）
      try {
        localStorage.setItem("grip:ortThreads", "1");
      } catch {
        // localStorage 不可用时忽略（ORT 将按 crossOriginIsolated 决定线程数）
      }
      return null;
    }
    try {
      const w = new Worker(new URL("./infer/worker.ts", import.meta.url), {
        type: "module",
      });
      w.onmessage = (ev: MessageEvent<InferWorkerMessage>) =>
        this.onWorkerMessage(ev.data);
      w.onerror = (ev: ErrorEvent) => {
        // 脚本加载失败等：停用 worker，拒绝在途请求（调用方回退主线程路径）
        this.inferWorker = null;
        this.stopWorkerWatchdog();
        const pend = this.workerPending;
        if (pend) {
          this.workerPending = null;
          this.workerModelIds.clear();
          pend.reject(new Error(ev.message || "推理 Worker 加载失败"));
        }
      };
      this.inferWorker = w;
    } catch {
      this.inferWorker = null;
    }
    return this.inferWorker;
  }

  private onWorkerMessage(m: InferWorkerMessage): void {
    if (m.type === "models") {
      for (const id of m.ids) this.workerModelIds.add(id);
      return;
    }
    const pend = this.workerPending;
    if (!pend || m.id !== pend.id) return;
    if (m.type === "stage") {
      this.workerStageAt = performance.now(); // #15 看门狗表针
      this.workerSlowStage = m.name;
      this.showScan(m.name, m.pct); // 扫描层由 onmessage 驱动（含确定性百分比）
      return;
    }
    this.workerPending = null;
    this.stopWorkerWatchdog();
    if (m.type === "done") {
      const win = window as unknown as { __gripInferMeta?: InferMeta };
      win.__gripInferMeta = m.meta;
      pend.resolve(m.analysis);
    } else {
      pend.reject(new Error(m.message));
    }
  }

  /**
   * Worker 路径分析：rgba 与模型 bytes 均传「拷贝后 transfer」，失败时主线程
   * 仍持有原件，可直接回退现有主线程推理路径（runAnalysis 内 try/catch）。
   * opts：#15 运行时降级开关（移动端免 WebGPU / 停滞重试单线程）。
   */
  private async analyzeViaWorker(
    w: Worker,
    full: Uint8ClampedArray,
    fw: number,
    fh: number,
    p: Params,
    opts?: { ep?: "wasm"; threads?: number },
  ): Promise<Analysis> {
    const bytes: NonNullable<AnalyzeRequest["modelBytes"]> = {};
    const transfer: Transferable[] = [];
    const wanted: Array<[keyof typeof bytes, string]> = [
      ["yolo", "yolo-holds"],
      ["enc", "sam-encoder"],
      ["dec", "sam-decoder"],
      ["person", "person-detector"],
    ];
    for (const [slot, id] of wanted) {
      if (this.workerModelIds.has(id)) continue; // bytes 只传一次
      const b = await this.modelBytes(id); // manifest 缺该模型时为 null（person 可选）
      if (b) {
        bytes[slot] = b.slice(0);
        transfer.push(bytes[slot]!);
      }
    }
    const rgba = new Uint8ClampedArray(full);
    transfer.push(rgba.buffer);
    this.showScan("启动推理引擎");
    return new Promise<Analysis>((resolve, reject) => {
      const id = ++this.workerSeq;
      this.workerPending = { id, resolve, reject };
      const req: AnalyzeRequest = {
        type: "analyze",
        id,
        rgba: rgba.buffer,
        w: fw,
        h: fh,
        params: p,
      };
      if (Object.keys(bytes).length > 0) req.modelBytes = bytes;
      if (opts) req.ortOptions = opts;
      this.startWorkerWatchdog(opts?.threads); // #15：stage 停滞超时 terminate + 降级重试
      w.postMessage(req, transfer);
    });
  }

  private async ensureModels(): Promise<void> {
    if (!this.manifest) this.manifest = await loadManifest();
    const m = this.manifest;
    const ids = ["yolo-holds", "sam-encoder", "sam-decoder", "person-detector"];
    const total = ids.length;
    for (let mi = 0; mi < ids.length; mi++) {
      const id = ids[mi];
      const entry = m.models[id];
      if (!entry) continue;
      const label = entry.file.split(".")[0];
      this.showScan(`准备模型 ${label}（${mi + 1}/${total}）`, mi / total);
      const bytes = await ensureModel(id, entry, {
        base: m.base, mirrors: m.mirrors,
        onProgress: (p) => {
          if (p.phase === "downloading") {
            // 整体进度 = 已完成模型数 + 当前文件内进度（真百分比，控制感）
            const filePct = p.total > 0 ? Math.min(p.loaded / p.total, 1) : 0;
            this.showScan(
              `下载 ${label}（${mi + 1}/${total}）` +
                ` ${(p.loaded / 1048576).toFixed(1)}/${(p.total / 1048576).toFixed(1)} MB`,
              (mi + filePct) / total,
            );
          } else {
            this.showScan(`${p.phase === "verifying" ? "校验" : "缓存"} ${label}`, (mi + 0.97) / total);
          }
        },
      });
      this.showScan(`就绪 ${label}（${mi + 1}/${total}）`, (mi + 1) / total);
      // T5：会话创建移入推理 Worker；主线程只缓存 bytes（降级路径由 runAnalysis 惰性建会话）
      this.modelBytesCache.set(id, bytes);
    }
  }

  private async runAnalysis(buf?: ArrayBuffer): Promise<void> {
    if (!this.file) return;
    this.busy = true;
    try {
      const t0 = performance.now();
      const raw = buf ?? (await this.file.blob.arrayBuffer());
      const bmp = await createImageBitmap(new Blob([raw]));
      const cv = document.createElement("canvas");
      cv.width = bmp.width;
      cv.height = bmp.height;
      const ctx = cv.getContext("2d", { willReadFrequently: true })!;
      ctx.drawImage(bmp, 0, 0);
      const full = ctx.getImageData(0, 0, bmp.width, bmp.height).data;

      const p = { ...this.params, analysisMaxDim: this.effectiveMaxDim() };
      // T5 + #15：推理优先走 Worker（主线程保持可交互）；停滞（看门狗 terminate）
      // 或失败时逐级降级：Worker(默认/移动端免 WebGPU) → Worker(单线程 wasm)
      // → 主线程（localStorage 钩子已置，同为单线程 wasm）。
      let result: Analysis | null = null;
      const worker1 = this.ensureInferWorker();
      if (worker1) {
        try {
          result = await this.analyzeViaWorker(
            worker1, full, bmp.width, bmp.height, p, this.baseOrtOptions(),
          );
        } catch (e) {
          this.workerModelIds.clear(); // 保守：下次分析重传模型 bytes
          if (e instanceof InferStallError && !this.inferDegradedTried) {
            // 停滞 → 自动降级重试一次（单线程 wasm：规避 pthread/WebGPU 挂起两类根因）
            this.inferDegradedTried = true;
            this.persistDegradedHooks(); // 主线程兜底路径与下次加载同享稳态配置
            this.toast(`「${e.stage}」停滞 ${e.seconds}s，已切换单线程模式重试`);
            const worker2 = this.ensureInferWorker(); // 看门狗已 terminate → 重建
            if (worker2) {
              try {
                result = await this.analyzeViaWorker(
                  worker2, full, bmp.width, bmp.height, p, { ep: "wasm", threads: 1 },
                );
              } catch (e2) {
                console.warn("[grip] 单线程 Worker 重试仍失败，回退主线程：", e2);
                this.workerModelIds.clear();
              }
            }
          } else {
            console.warn("[grip] Worker 推理失败，回退主线程路径：", e);
            if (e instanceof InferStallError) {
              this.toast(`推理停滞 ${e.seconds}s，已回退主线程单线程推理（较慢）`);
            }
          }
        }
      } else {
        console.warn("[grip] 无 Worker 环境：降级主线程（单线程推理）");
        this.toast("当前环境不支持 Web Worker，已降级主线程单线程推理");
      }
      (window as unknown as Record<string, string>).__gripInfer = result ? "worker" : "main";
      this.analysis =
        result ??
        (await analyze(full, bmp.width, bmp.height, {
          yolo: await this.mtSession("yolo-holds", "YOLO 岩点检测", 1, 4),
          samEncoder: await this.mtSession("sam-encoder", "SAM 编码器", 2, 4),
          samDecoder: await this.mtSession("sam-decoder", "SAM 解码器", 3, 4),
          person: (await this.modelBytes("person-detector"))
            ? await this.mtSession("person-detector", "人物检测 yolo11n", 4, 4)
            : undefined,
        }, p, (stage, pct) => this.showScan(stage, pct)));
      this.params = p;
      this.selected = null;
      this.excluded = new Set();
      this.erased = new Set();
      this.undoStack = [];
      this.enterEdit(performance.now() - t0, false);
    } catch (e) {
      // 全链路失败：退出 busy（不再卡死整个应用），保留照片预览供重新选图
      console.error("[grip] 分析失败：", e);
      this.toast(`分析失败：${e instanceof Error ? e.message.slice(0, 60) : String(e)}`);
      this.hideScan();
    } finally {
      this.busy = false;
    }
  }

  /** 主线程降级路径建会话：移动端启发式或已持久化降级时跳过 WebGPU 探测。
   *  #15 第三轮：逐模型上屏进度——主线程建会话（图优化/编译）可能数十秒，
   *  静默期扫描层会停留在 worker 的旧文案上，表象即「卡在 创建推理会话 i/N」 */
  private async mtSession(id: string, label: string, i: number, n: number) {
    this.showScan(`创建推理会话（主线程）${i}/${n}：${label}`, (i - 0.5) / n);
    const bytes = (await this.modelBytes(id))!;
    const noGpu = this.mobileHeuristic() || this.persistentDegraded();
    return getSession(id, bytes, noGpu ? { preferWebGpu: false } : {});
  }

  private modelBytesCache = new Map<string, ArrayBuffer>();
  private async modelBytes(id: string): Promise<ArrayBuffer | null> {
    if (!this.manifest) this.manifest = await loadManifest();
    const entry = this.manifest.models[id];
    if (!entry) return null;
    if (!this.modelBytesCache.has(id)) {
      this.modelBytesCache.set(
        id,
        await ensureModel(id, entry, { base: this.manifest.base, mirrors: this.manifest.mirrors }),
      );
    }
    return this.modelBytesCache.get(id)!;
  }

  private async redetect(): Promise<void> {
    if (!this.file || this.busy) return;
    await deleteSession(this.file.sha).catch(() => undefined);
    await this.runAnalysis();
  }

  private enterEdit(timingMs: number, restored: boolean): void {
    this.hideScan();
    document.getElementById("empty-state")!.hidden = true;
    document.getElementById("view-controls")!.hidden = false;
    document.getElementById("hint-bar")!.hidden = false;
    document.getElementById("route-strip")!.hidden = false;
    document.getElementById("dock")!.hidden = false;
    (document.getElementById("btn-export") as HTMLButtonElement).disabled = false;
    this.view.setAnalysis(this.analysis!);
    this.view.overlayStyle.selected = this.selected;
    this.view.overlayStyle.excluded = this.excluded;
    this.view.overlayStyle.erased = this.erased;
    this.setTool("select");
    this.refreshPreview();
    this.renderRoutes();
    this.buildPersonLookup();
    this.syncParamsSheet();
    this.syncDesktopPane();
    if (!restored) this.autosave(timingMs);
    // 桌面语义：未选线时视图落原图
    this.view.setShowResult(this.selected !== null);
  }

  // ---------- 编辑操作（全部可撤销，变更前 push） ----------
  private pushUndo(): void {
    this.undoStack.push({
      selected: this.selected,
      excluded: new Set(this.excluded),
      erased: new Set(this.erased),
      // P1#3：holds 浅拷即可——快照后可变字段（cluster 等）改在 live 对象上，
      // 浅拷对象天然隔离；ownPixels 检出后只读，共享无损且省掉整段深拷贝。
      // clusters 含被 rebuildClusters 重写的成员表，必须保持深拷。
      holds: this.analysis!.holds.map((h) => ({ ...h })),
      clusters: structuredClone(this.analysis!.clusters),
    });
    if (this.undoStack.length > 50) this.undoStack.shift();
    (document.getElementById("btn-undo") as HTMLButtonElement).disabled = false;
  }

  private undo(): void {
    const snap = this.undoStack.pop();
    if (!snap) {
      this.toast("没有可撤销的操作");
      return;
    }
    this.selected = snap.selected;
    this.excluded = snap.excluded;
    this.erased = snap.erased;
    this.analysis!.holds = snap.holds;
    this.analysis!.clusters = snap.clusters;
    (document.getElementById("btn-undo") as HTMLButtonElement).disabled =
      this.undoStack.length === 0;
    this.afterEdit("已撤销");
  }

  private syncOverlay(): void {
    this.view.overlayStyle.selected = this.selected;
    this.view.overlayStyle.excluded = this.excluded;
    this.view.overlayStyle.erased = this.erased;
    this.view.draw();
  }

  private afterEdit(msg?: string): void {
    this.syncOverlay();
    this.refreshPreview();
    this.renderRoutes();
    this.autosave();
    if (msg) this.toast(msg);
  }

  private onTap(p: { x: number; y: number }): void {
    if (!this.analysis || this.busy) return;
    const hit = this.view.hitTest(p.x, p.y);
    switch (this.view.tool) {
      case "select": {
        // 点中攀岩者区域 → 切换保色开关（优先于岩点命中，点人不选线）
        const lut = this.personLookup;
        const a = this.analysis;
        const ix = Math.floor(p.x);
        const iy = Math.floor(p.y);
        if (lut && a && ix >= 0 && iy >= 0 && ix < a.aw && iy < a.ah && lut[iy * a.aw + ix]) {
          this.togglePersonKeep();
          return;
        }
        if (!hit || hit.cluster < 0) return;
        if (this.selected === hit.cluster) return;
        this.pushUndo();
        this.selected = hit.cluster;
        this.view.setShowResult(true);
        this.afterEdit();
        break;
      }
      case "lasso": {
        // 套索模式下点击：已选线路 → 该点并入；未选 → 等效选线
        if (!hit) return;
        if (this.selected !== null) {
          if (hit.cluster === this.selected) return;
          this.pushUndo();
          hit.cluster = this.selected;
          this.rebuildClusters();
          this.afterEdit("已并入所选线路");
        } else if (hit.cluster >= 0) {
          this.pushUndo();
          this.selected = hit.cluster;
          this.view.setShowResult(true);
          this.afterEdit();
        }
        break;
      }
      case "erase": {
        if (!hit) return;
        this.pushUndo();
        if (this.erased.has(hit.id)) this.erased.delete(hit.id);
        else {
          this.erased.add(hit.id);
          this.excluded.delete(hit.id);
        }
        this.afterEdit("已切换岩点抹除状态");
        break;
      }
      case "exclude": {
        if (!hit) return;
        this.pushUndo();
        if (this.excluded.has(hit.id)) {
          this.excluded.delete(hit.id);
          this.afterEdit("已恢复：该岩点重新入线保色");
        } else {
          this.excluded.add(hit.id);
          this.erased.delete(hit.id);
          // #14：排除的视觉反馈因归组语义而异，必须给用户可感知的确切说明——
          // 仅选中线路内的排除点会灰度化 + 琥珀框；未选线时画面本就是原图/全灰，
          // 只有琥珀框；排除其它线路的点是数据层操作，当前视图不变属预期。
          const inRoute = this.selected !== null && hit.cluster === this.selected;
          const msg =
            this.selected === null
              ? "已排除：琥珀框标记（选线后该点不再保色），再点恢复"
              : inRoute
                ? "已排除：已灰度化 + 琥珀框标记，再点恢复"
                : "已排除：该点不在当前线路，画面不变属预期（其所属线路被选中时生效）";
          this.afterEdit(msg);
        }
        break;
      }
    }
  }

  /** 偶奇规则多边形光栅化（对拍 raster_poly）→ 全图 0/255 region */
  private rasterPoly(pts: Array<{ x: number; y: number }>): Uint8Array {
    const a = this.analysis!;
    const n = a.aw * a.ah;
    const mask = new Uint8Array(n);
    let y0 = Infinity;
    let y1 = -Infinity;
    for (const p of pts) {
      y0 = Math.min(y0, p.y);
      y1 = Math.max(y1, p.y);
    }
    const iy0 = Math.max(0, Math.floor(y0));
    const iy1 = Math.min(a.ah - 1, Math.ceil(y1));
    for (let y = iy0; y <= iy1; y++) {
      const xs: number[] = [];
      for (let i = 0; i < pts.length; i++) {
        const p1 = pts[i];
        const p2 = pts[(i + 1) % pts.length];
        if (p1.y === p2.y) continue;
        const yy = y + 0.5;
        if (yy >= Math.min(p1.y, p2.y) && yy < Math.max(p1.y, p2.y)) {
          xs.push(p1.x + ((yy - p1.y) / (p2.y - p1.y)) * (p2.x - p1.x));
        }
      }
      xs.sort((m, n2) => m - n2);
      for (let k = 0; k + 1 < xs.length; k += 2) {
        const ix0 = Math.max(0, Math.round(xs[k]));
        const ix1 = Math.min(a.aw - 1, Math.round(xs[k + 1]) - 1);
        for (let x = ix0; x <= ix1; x++) mask[y * a.aw + x] = 255;
      }
    }
    return mask;
  }

  private onLasso(pts: Array<{ x: number; y: number }>): void {
    if (!this.analysis || this.busy) return;
    const region = this.rasterPoly(pts);
    const inside = (h: Hold): boolean => {
      const cx = h.centroid[0];
      const cy = h.centroid[1];
      const ix = Math.floor(cx);
      const iy = Math.floor(cy);
      return ix >= 0 && iy >= 0 && ix < this.analysis!.aw && iy < this.analysis!.ah
        ? region[iy * this.analysis!.aw + ix] > 0
        : false;
    };
    void this.lassoAdd(region, inside);
  }

  private async lassoAdd(
    region: Uint8Array,
    inside: (h: Hold) => boolean,
  ): Promise<void> {
    const a = this.analysis!;
    this.busy = true;
    this.showScan("套索补测");
    const found = detectInRegion(a.img, a.aw, a.ah, region, this.params);
    this.pushUndo();
    let added = 0;
    const force = this.selected;
    // 去重：质心距 < (r+r')×0.75 跳过
    for (const f of found) {
      const r = Math.sqrt(f.area / Math.PI);
      let dup = false;
      for (const e of a.holds) {
        const re = Math.sqrt(e.area / Math.PI);
        if (
          Math.hypot(e.centroid[0] - f.centroid[0], e.centroid[1] - f.centroid[1]) <
          (r + re) * 0.75
        ) {
          dup = true;
          break;
        }
      }
      if (dup) continue;
      f.id = a.holds.length;
      if (force !== null) {
        f.cluster = force;
      } else {
        f.cluster = assignHold(a.clusters, f, this.params.colorTolAb, f.id);
      }
      a.holds.push(f);
      added++;
    }
    // 已选线路：圈内已有岩点强制改归
    if (force !== null) {
      for (const h of a.holds) {
        if (
          h.cluster !== force &&
          h.cluster >= 0 &&
          !this.excluded.has(h.id) &&
          !this.erased.has(h.id) &&
          inside(h)
        ) {
          h.cluster = force;
          added++;
        }
      }
    }
    this.rebuildClusters();
    this.busy = false;
    this.hideScan();
    this.afterEdit(
      force !== null ? `圈选补测：${added} 个岩点并入所选线路` : `圈选补测：检测 ${added} 个岩点`,
    );
  }

  /** 由 holds[].cluster 重建 clusters 的成员表与面积（色心/色卡不重算——force 语义） */
  private rebuildClusters(): void {
    const a = this.analysis!;
    const selMembers =
      this.selected !== null ? new Set(a.clusters[this.selected]?.holds ?? []) : null;
    for (const c of a.clusters) {
      c.holds = [];
      c.area = 0;
    }
    for (const h of a.holds) {
      if (h.cluster >= 0 && h.cluster < a.clusters.length) {
        a.clusters[h.cluster].holds.push(h.id);
        a.clusters[h.cluster].area += h.area;
      }
    }
    a.clusters.sort((x, y) => y.area - x.area);
    for (let ci = 0; ci < a.clusters.length; ci++) {
      for (const hi of a.clusters[ci].holds) a.holds[hi].cluster = ci;
    }
    if (selMembers && selMembers.size > 0) {
      const anchor = [...selMembers][0];
      const idx = a.clusters.findIndex((c) => c.holds.includes(anchor));
      this.selected = idx >= 0 ? idx : null;
    } else if (selMembers) {
      this.selected = null;
    }
  }

  // ---------- 预览 ----------
  refreshPreview(): void {
    const a = this.analysis;
    if (!a) return;
    const sel = this.selected;
    let mask = keepMask(a, sel ?? undefined, this.excluded, this.erased, this.params.maskDilate);
    keepPerson(mask, a, this.params.personKeep);
    const f = feather(a, mask, this.params.feather);
    const fill = this.params.bgFill > 0 ? fillLayer(a, this.erased, this.params.bgFill) : null;
    const prev = compositePreview(a, f, this.params.bgSat, fill);
    this.view.setComposite(new ImageData(new Uint8ClampedArray(prev), a.aw, a.ah));
  }

  // ---------- 线路条 ----------
  private renderRoutes(): void {
    const a = this.analysis;
    const strip = document.getElementById("route-strip")!;
    strip.innerHTML = "";
    if (!a) return;
    const names = disambiguatedNames(a.clusters);
    const mk = (label: string, swatch: string | null, cnt: string, on: boolean, fn: () => void) => {
      const b = document.createElement("button");
      b.className = "route-chip" + (on ? " on" : "");
      b.innerHTML =
        (swatch ? `<span class="swatch" style="background:${swatch}"></span>` : "") +
        `<span>${label}</span><span class="cnt mono">${cnt}</span>`;
      b.addEventListener("click", fn);
      strip.appendChild(b);
    };
    mk("无", "linear-gradient(135deg,#9aa0a8,#e8eaee)", "全灰", this.selected === null, () => {
      if (this.selected === null) return;
      this.pushUndo();
      this.selected = null;
      this.view.setShowResult(false);
      this.afterEdit();
    });
    a.clusters.forEach((c, ci) => {
      mk(
        names[ci] ?? clusterName(c),
        `rgb(${c.rgb[0]},${c.rgb[1]},${c.rgb[2]})`,
        `${c.holds.length}点`,
        this.selected === ci,
        () => {
          if (this.selected === ci) return;
          this.pushUndo();
          this.selected = ci;
          this.view.setShowResult(true);
          this.afterEdit();
        },
      );
    });
  }

  // ---------- 导出 ----------
  private async export(): Promise<void> {
    const a = this.analysis;
    if (!a || !this.file || this.busy) return;
    this.busy = true;
    this.showScan("导出全分辨率 PNG");
    try {
      const bmp = await createImageBitmap(this.file.blob);
      const cv = document.createElement("canvas");
      cv.width = a.fw;
      cv.height = a.fh;
      const ctx = cv.getContext("2d")!;
      ctx.drawImage(bmp, 0, 0);
      const full = ctx.getImageData(0, 0, a.fw, a.fh);
      let mask = keepMask(a, this.selected ?? undefined, this.excluded, this.erased, this.params.maskDilate);
      keepPerson(mask, a, this.params.personKeep);
      const f = feather(a, mask, this.params.feather);
      const fill = this.params.bgFill > 0 ? fillLayer(a, this.erased, this.params.bgFill) : null;
      const out = compositeFull(full.data, a, f, this.params.bgSat, fill);
      if (this.params.outline > 0 && this.selected !== null) {
        const band = outlineBandFull(a, f);
        applyOutline(out, band, this.params.outline);
      }
      ctx.putImageData(new ImageData(new Uint8ClampedArray(out), a.fw, a.fh), 0, 0);
      const blob = await new Promise<Blob | null>((r) => cv.toBlob(r, "image/png"));
      if (blob) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${this.file.name.replace(/\.[^.]+$/, "")}_route.png`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        this.toast("已导出 PNG");
      }
    } finally {
      this.busy = false;
      this.hideScan();
    }
  }

  // ---------- 会话 ----------
  /** 立即落盘（绕过防抖；pagehide 时救命路径） */
  private doSave(timingMs?: number): void {
    if (!this.analysis || !this.file) return;
    const sha = this.file.sha;
    const a = this.analysis!;
    void saveSession({
      id: sha,
      name: this.file.name,
      savedAt: Date.now(),
      params: this.params,
      selected: this.selected,
      excluded: [...this.excluded],
      erased: [...this.erased],
      person: a.person,
      holds: a.holds,
      clusters: a.clusters,
      labels: a.labels,
      aw: a.aw,
      ah: a.ah,
      fw: a.fw,
      fh: a.fh,
      wallV: a.wallV,
      wallS: a.wallS,
      timingMs: timingMs ?? a.timingMs,
      img: a.img,
      origBlob: this.file.blob,
    })
      .then(() => {
        // P1#2：IDB 写成功才写指针——避免 pagehide 时 IDB 写被中止而
        // grip:last 已指向缺失记录（下次启动恢复拿不到会话）。
        localStorage.setItem("grip:last", sha);
      })
      .catch(() => undefined);
  }

  private autosave(timingMs?: number): void {
    if (!this.analysis || !this.file) return;
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = window.setTimeout(() => this.doSave(timingMs), 400);
  }

  /** pagehide 救命路径：清掉未决防抖立即写 */
  flushSave(): void {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    this.doSave();
  }

  // ---------- 杂项 UI ----------
  private updateTitle(): void {
    const el = document.getElementById("file-title")!;
    el.textContent = this.file
      ? `${this.file.name} · ${Math.round(this.view.zoom * 100)}%`
      : "未选择照片";
  }

  toast(msg: string): void {
    const t = document.getElementById("toast")!;
    t.textContent = msg;
    t.hidden = false;
    setTimeout(() => (t.hidden = true), 2200);
  }

  /**
   * 扫描层文案（+可选确定性进度 0..1）。pct 提供 → 进度条切确定性模式并显示
   * 百分比读数（2026-10-22 用户决议：下载/建会话/SAM 逐框给真百分比，控制感）；
   * 未提供 → 不定动画（无法计量的阶段）。
   */
  private scanPct: number | null = null;
  showScan(stage: string, pct?: number): void {
    const ov = document.getElementById("scan-overlay")!;
    if (ov.hidden) {
      ov.hidden = false;
      this.scanT0 = performance.now();
      if (this.scanTimer) clearInterval(this.scanTimer);
      this.scanTimer = window.setInterval(() => {
        const pctTxt =
          this.scanPct === null ? "" : `${Math.round(this.scanPct * 100)}% · `;
        document.getElementById("scan-elapsed")!.textContent =
          `${pctTxt}${((performance.now() - this.scanT0) / 1000).toFixed(1)}s`;
      }, 100);
    }
    document.getElementById("scan-stage")!.textContent = stage;
    const bar = document.getElementById("scan-bar")!;
    const fill = bar.querySelector("i") as HTMLElement;
    if (pct !== undefined && Number.isFinite(pct)) {
      this.scanPct = Math.min(Math.max(pct, 0), 1);
      bar.classList.add("det");
      const shown = `${Math.round(this.scanPct * 100)}%`;
      fill.style.width = shown;
      bar.dataset.pct = shown;
    } else {
      this.scanPct = null;
      bar.classList.remove("det");
      fill.style.width = "";
      delete bar.dataset.pct;
    }
  }

  hideScan(): void {
    document.getElementById("scan-overlay")!.hidden = true;
    if (this.scanTimer) {
      clearInterval(this.scanTimer);
      this.scanTimer = null;
    }
  }
}

// 参数面板（滑杆生成）独立于 App 生命周期，由 App 实例驱动
const app = new App();
// pagehide 立即落盘：防抖窗口内刷新/被杀不丢会话
window.addEventListener("pagehide", () => app.flushSave());
void bootRestore();

/** 启动恢复最近会话（桌面版「重开文件夹」的 web 等价物） */
async function bootRestore(): Promise<void> {
  const last = localStorage.getItem("grip:last");
  if (!last) return;
  const stored = await loadSession(last).catch(() => undefined);
  if (!stored) {
    localStorage.removeItem("grip:last");
    return;
  }
  await app.restoreFrom(stored);
}
